import { createReducer, on } from '@ngrx/store';

import { createRequestError, ResourceStatus, ResourceStatuses } from '@platform/shared/resource.state';

import * as FiduciaryActions from './fiduciary.actions';
import { initialFiduciaryState } from './fiduciary.state';

export const fiduciaryReducer = createReducer(
  initialFiduciaryState,
  on(FiduciaryActions.uploadFiduciaryInboxFiles, (state, { targetInbox }) => {
    const newState = {
      ...state,
      abacusInboxes: {
        ...state.abacusInboxes,
      },
    };

    newState.abacusInboxes[targetInbox] = { ...state.abacusInboxes[targetInbox] };
    newState.abacusInboxes[targetInbox].status = ResourceStatuses.PENDING;

    return newState;
  }),

  on(FiduciaryActions.uploadFiduciaryInboxFilesSucceeded, (state, { targetInbox }) => {
    const newState = {
      ...state,
      abacusInboxes: {
        ...state.abacusInboxes,
      },
    };

    newState.abacusInboxes[targetInbox] = { ...state.abacusInboxes[targetInbox] };
    newState.abacusInboxes[targetInbox].status = ResourceStatuses.SUCCEEDED;
    newState.abacusInboxes[targetInbox].error = null!;

    return newState;
  }),

  on(FiduciaryActions.uploadFiduciaryInboxFilesFailed, (state, { targetInbox, status, errors }) => {
    const newState = {
      ...state,
      abacusInboxes: {
        ...state.abacusInboxes,
      },
    };

    newState.abacusInboxes[targetInbox] = { ...state.abacusInboxes[targetInbox] };
    newState.abacusInboxes[targetInbox].status = ResourceStatuses.FAILED;
    newState.abacusInboxes[targetInbox].error = createRequestError(status, errors);

    return newState;
  }),

  on(FiduciaryActions.downloadFiduciaryInboxFile, (state) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      documentDownload: {
        ...state.inboxUploadHistory.documentDownload,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.downloadFiduciaryInboxFileSucceeded, (state, { content }) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      documentDownload: {
        ...state.inboxUploadHistory.documentDownload,
        data: content,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.downloadFiduciaryInboxFileFailed, (state, { status, errors }) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      documentDownload: {
        ...state.inboxUploadHistory.documentDownload,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.loadAllFiduciaryTasksForUser, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      allTasksForUser: {
        ...state.portalTasks.allTasksForUser,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.loadAllFiduciaryTasksForUserSucceeded, (state, { tasks }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      allTasksForUser: {
        data: tasks,
        status: ResourceStatuses.SUCCEEDED,
        error: null,
      },
    },
  })),

  on(FiduciaryActions.loadAllFiduciaryTasksForUserFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      allTasksForUser: {
        ...state.portalTasks.allTasksForUser,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.createTask, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.createTaskSucceeded, (state, { task }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        data: task,
        status: ResourceStatuses.NONE,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.createTaskFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.createTaskComment, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        ...state.portalTasks.taskComments,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.createTaskCommentSucceeded, (state, { comments }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        data: comments,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.createTaskCommentFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        ...state.portalTasks.taskComments,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.updateTask, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.updateTaskSucceeded, (state, { task }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        data: task,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.updateTaskFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.deleteTask, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.deleteTaskSucceeded, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        data: null!,
        status: ResourceStatuses.NONE,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.deleteTaskFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.deleteTaskComment, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        ...state.portalTasks.taskComments,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.deleteTaskCommentSucceeded, (state, { comments }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        data: comments,
        status: ResourceStatuses.SUCCEEDED as ResourceStatus,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.deleteTaskCommentFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        ...state.portalTasks.taskComments,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.loadTask, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.loadTaskSucceeded, (state, { task }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        data: task,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.loadTaskFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.loadTaskCommentsSucceeded, (state, { comments }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskComments: {
        data: comments,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.loadTaskDocumentsSucceeded, (state, { documents }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        data: documents,
        status: ResourceStatuses.SUCCEEDED as ResourceStatus,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.uploadDocument, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        ...state.portalTasks.taskDocuments,
        status: ResourceStatuses.PENDING,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.uploadDocumentSucceeded, (state, { documents }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        data: documents,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.uploadDocumentFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        ...state.portalTasks.taskDocuments,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.deleteDocument, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        ...state.portalTasks.taskDocuments,
        status: ResourceStatuses.PENDING,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.deleteDocumentSucceeded, (state, { documents }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        data: documents,
        status: ResourceStatuses.SUCCEEDED as ResourceStatus,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.deleteDocumentFailed, (state, { status, errors }) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        ...state.portalTasks.taskDocuments,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),

  on(FiduciaryActions.resetTaskDetail, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDetail: {
        ...state.portalTasks.taskDetail,
        data: null!,
        status: ResourceStatuses.NONE,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.resetTaskDocuments, (state) => ({
    ...state,
    portalTasks: {
      ...state.portalTasks,
      taskDocuments: {
        ...state.portalTasks.taskDocuments,
        data: null!,
        status: ResourceStatuses.NONE as ResourceStatus,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.loadInboxUploadHistory, (state) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      entries: {
        ...state.inboxUploadHistory.entries,
        status: ResourceStatuses.PENDING,
      },
    },
  })),

  on(FiduciaryActions.loadInboxUploadHistorySucceeded, (state, { histories }) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      entries: {
        ...state.inboxUploadHistory.entries,
        data: histories,
        status: ResourceStatuses.SUCCEEDED,
        error: null!,
      },
    },
  })),

  on(FiduciaryActions.loadInboxUploadHistoryFailed, (state, { status, errors }) => ({
    ...state,
    inboxUploadHistory: {
      ...state.inboxUploadHistory,
      entries: {
        ...state.inboxUploadHistory.entries,
        status: ResourceStatuses.FAILED,
        error: createRequestError(status, errors),
      },
    },
  })),
);
